import { DownloadIcon } from '@radix-ui/react-icons';
import { Badge, Box, Button, Flex, Grid, Heading } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonBackArrow } from 'components/common/buttons/back-arrow';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonDateRangeInput } from 'components/common/form/date/range';
import { CommonSearchInput } from 'components/common/form/search';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { CommonTableHoC } from 'components/common/table';
import {
  MAX_DATE,
  MIN_DATE,
  PAGE_SIZES,
} from 'components/sections/admin-portal/machines/dialogs/settings/machine-session-history/common';
import { MachineSessionEventDetails } from 'components/sections/admin-portal/machines/dialogs/settings/machine-session-history/machine-session-event-details';
import env from 'config';
import { isWithinInterval, parseISO } from 'date-fns';
import {
  MachineSettingsQueryParams,
  MachineSettingsSection,
} from 'enums/machine-settings';
import { t } from 'i18next';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSelectable } from 'interfaces/tables/selection';
import { ITableSortable } from 'interfaces/tables/sorting';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { UserRole } from 'lib_ts/enums/auth.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ISessionEventDetails } from 'lib_ts/interfaces/i-session-event';
import { ISessionSummary } from 'lib_ts/interfaces/i-session-summary';
import { compact } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { paths } from 'routes/paths';
import { SessionEventsService } from 'services/session-events.service';
import { useAuthStore } from 'stores/use-auth-store';

interface ISessionDetailsFilters {
  tag: string[];
  startDate?: Date;
  endDate?: Date;
}

interface IProps {
  session: ISessionSummary;
  onBack: () => void;
  onExportSession: () => void;
  exportInProgress: boolean;
}

const COLUMNS: ITypedTableColumn<ISessionEventDetails>[] = [
  {
    label: 'admin.event-tag',
    key: 'event-tag',
    formatFn: (s) => <Badge color={RADIX.COLOR.NEUTRAL}>{s.tags}</Badge>,
  },
  {
    label: 'admin.time',
    key: '_created',
    dataType: 'datetime',
  },
];

const OPEN_LIVE_FIRES = false;

const getLiveFireURL = (machineID: string, id: string) => {
  const params = new URLSearchParams({
    [MachineSettingsQueryParams.MachineID]: machineID,
    [MachineSettingsQueryParams.SectionID]: MachineSettingsSection.LiveFires,
    [MachineSettingsQueryParams.FireID]: id,
  });

  return `${env.self_url}${paths.admin.machines}?${params.toString()}`;
};

export const MachineSessionDetails = (props: IProps) => {
  const current = useAuthStore((s) => s.current);
  const { onBack, session, onExportSession, exportInProgress } = props;
  const [loading, setLoading] = useState(false);

  const [selectedEvent, setSelectedEvent] = useState<ISessionEventDetails>();
  const [sessionEvents, setSessionEvents] = useState<ISessionEventDetails[]>(
    []
  );
  const [filter, setFilter] = useState<ISessionDetailsFilters>({
    tag: [],
    startDate: undefined,
    endDate: undefined,
  });

  useEffect(() => {
    const fetchSessionEvents = async () => {
      try {
        setLoading(true);
        const result = await SessionEventsService.getManyFromSession({
          env: session.env,
          machineID: session.machineID,
          session: session.sessionID,
        });

        setSessionEvents(result);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    fetchSessionEvents();
  }, [session]);

  const tagOptions = useMemo(
    () =>
      compact(ArrayHelper.unique(sessionEvents.map((m) => m.tags))).map(
        (m) => ({
          label: m,
          value: m,
        })
      ),
    [sessionEvents]
  );

  const filtered = useMemo(() => {
    return sessionEvents
      .filter((m) => {
        if (!filter.startDate || !filter.endDate) {
          return true;
        }

        const isBetweenDates = isWithinInterval(parseISO(m._created), {
          start: filter.startDate,
          end: filter.endDate,
        });

        return isBetweenDates;
      })
      .filter((m) => {
        if (!filter.tag.length) {
          return true;
        }
        if (!m.tags) {
          return false;
        }
        return filter.tag.includes(m.tags);
      });
  }, [sessionEvents, filter]);

  const applyFilter = (v: Partial<ISessionDetailsFilters>) => {
    setFilter((prev) => ({
      ...prev,
      ...v,
    }));
  };

  const sort: ITableSortable = {
    enableSort: true,
    defaultSort: {
      key: '_created',
      dir: 1,
    },
  };

  const select: ITableSelectable = {
    enableSelect: true,
    afterChangeSelected: (model?: ISessionEventDetails) => {
      // regular users can't see live fires component
      const openLiveFire =
        OPEN_LIVE_FIRES &&
        current.role === UserRole.admin &&
        model?.tags === 'fire';

      if (openLiveFire) {
        window.open(
          getLiveFireURL(session.machineID, model?._id ?? ''),
          '_blank'
        );
        return;
      }

      setSelectedEvent(model);
    },
  };

  const pagination: ITablePageable = {
    paginationID: PaginationID.AdminSessionDetails,
    total: filtered.length,
    enablePagination: true,
    pageSizes: PAGE_SIZES,
  };

  return (
    <ErrorBoundary identifier="MachineDetailsErrorsTab">
      <Flex
        direction="column"
        gap={RADIX.FLEX.GAP.MD}
        className={RADIX.VFLEX.GROW}
      >
        {selectedEvent && (
          <MachineSessionEventDetails
            session={session}
            event={selectedEvent}
            onBack={() => setSelectedEvent(undefined)}
          />
        )}
        <Flex
          direction="column"
          gap={RADIX.FLEX.GAP.SM}
          className={StringHelper.classNames([
            RADIX.VFLEX.GROW,
            /* always keep table in the DOM to persist input states */
            selectedEvent ? 'hidden' : '',
          ])}
        >
          <Flex gap={RADIX.FLEX.GAP.SM} align="center">
            <CommonBackArrow onClick={onBack} />

            <Heading size="4">{session.sessionID}</Heading>

            <Badge color={RADIX.COLOR.ACCENT}>{session.user}</Badge>

            <Button
              size={RADIX.BUTTON.SIZE.SM}
              disabled={exportInProgress}
              onClick={onExportSession}
              loading={exportInProgress}
              ml="auto"
            >
              <DownloadIcon /> {t('admin.export-session')}
            </Button>
          </Flex>
          <FlexTableWrapper
            gap={RADIX.FLEX.GAP.SECTION}
            header={
              <Grid columns="2" gap={RADIX.FLEX.GAP.SM}>
                <Box gridColumn="span 1">
                  <CommonSearchInput
                    id="machine-session-tag-filter"
                    placeholder="admin.event-tag"
                    options={tagOptions}
                    values={filter.tag}
                    onChange={(v) => applyFilter({ tag: v })}
                    multiple
                    strict
                  />
                </Box>
                <Box gridColumn="span 1">
                  <CommonDateRangeInput
                    id="machine-session-date-range-filter"
                    minDate={MIN_DATE}
                    maxDate={MAX_DATE}
                    placeholder="admin.date-range"
                    defaultStartValue={filter.startDate}
                    defaultEndValue={filter.endDate}
                    onChange={(values) => {
                      if (!values) {
                        NotifyHelper.warning({
                          message_md: t('common.check-inputs-msg'),
                        });
                        return;
                      }

                      if (!values.start || !values.end) {
                        applyFilter({
                          startDate: undefined,
                          endDate: undefined,
                        });
                        return;
                      }

                      applyFilter({
                        startDate: values.start,
                        endDate: values.end,
                      });
                    }}
                    showTime
                    optional
                  />
                </Box>
              </Grid>
            }
            table={
              <CommonTableHoC
                dataTestID="SessionDetails"
                identifier="SessionDetailsTable"
                displayColumns={COLUMNS}
                displayData={filtered}
                loading={loading}
                {...pagination}
                {...select}
                {...sort}
                vFlex
              />
            }
          />
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};
